import { fetchApi } from '@/services/api-client';

export type SalesReportPeriod = 'day' | 'week' | 'month';

export interface SalesBucket {
  /** Label — format depends on `period`: day/week -> 'YYYY-MM-DD', month -> 'YYYY-MM'. */
  period: string;
  total: number;
  walletTotal: number;
  thaiQrTotal: number;
  refundTotal: number;
  walletRefundTotal: number;
  thaiQrRefundTotal: number;
  netTotal: number;
  orderCount: number;
}

export interface SalesReport {
  period: SalesReportPeriod;
  from: string;
  to: string;
  buckets: SalesBucket[];
  grandTotal: number;
  walletTotal: number;
  thaiQrTotal: number;
  refundTotal: number;
  walletRefundTotal: number;
  thaiQrRefundTotal: number;
  netTotal: number;
  orderCount: number;
}

export interface SalesReportFilters {
  period: SalesReportPeriod;
  from?: string;
  to?: string;
}

export interface ProductSalesRow {
  productId: string;
  name: string;
  quantity: number;
  revenue: number;
  refundedQuantity: number;
  refundedRevenue: number;
  netQuantity: number;
  netRevenue: number;
}

export interface ProductSalesReport {
  from: string;
  to: string;
  rows: ProductSalesRow[];
  totalQuantity: number;
  totalRevenue: number;
  totalRefundedRevenue: number;
  totalNetRevenue: number;
}

export interface ProductSalesFilters {
  from?: string;
  to?: string;
}

const BASE = '/admin/cafeteria/reports';

export const reportsApi = {
  sales: (filters: SalesReportFilters) => {
    const params = new URLSearchParams();
    params.append('period', filters.period);
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    return fetchApi<SalesReport>(`${BASE}/sales?${params.toString()}`);
  },
  products: (filters: ProductSalesFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
    const qs = params.toString();
    return fetchApi<ProductSalesReport>(`${BASE}/products${qs ? `?${qs}` : ''}`);
  },
};

export type CafeteriaOverviewPreset = 'today' | 'week' | 'month' | 'custom';
export type CafeteriaOverviewGranularity = 'hour' | 'day' | 'week';

export interface CafeteriaOverviewDateSpan {
  /** YYYY-MM-DD in Bangkok, inclusive. */
  from: string;
  to: string;
}

export interface CafeteriaOverviewTotals {
  grandTotal: number;
  walletTotal: number;
  thaiQrTotal: number;
  refundTotal: number;
  walletRefundTotal: number;
  thaiQrRefundTotal: number;
  netTotal: number;
  orderCount: number;
  walletOrderCount: number;
  thaiQrOrderCount: number;
  refundedOrderCount: number;
  itemsSold: number;
  refundedItems: number;
  customerCount: number;
  anonymousOrderCount: number;
  avgOrderValue: number;
  itemsPerOrder: number;
  topUpCount: number;
  topUpTotal: number;
}

export interface CafeteriaOverviewBucket {
  /** 'HH' by the hour, 'YYYY-MM-DD' by the day or the Monday of a week. */
  key: string;
  total: number;
  refundTotal: number;
  netTotal: number;
  walletTotal: number;
  thaiQrTotal: number;
  orderCount: number;
}

export interface CafeteriaOverviewHour {
  hour: number;
  netTotal: number;
  orderCount: number;
}

export interface CafeteriaOverviewWeekday {
  /** ISO weekday, Monday = 1 .. Sunday = 7. */
  weekday: number;
  netTotal: number;
  orderCount: number;
  dayCount: number;
}

export type CafeteriaOverviewCustomerType = 'student' | 'staff' | 'parent' | 'anonymous';

export interface CafeteriaOverviewCustomerTypeRow {
  type: CafeteriaOverviewCustomerType;
  orderCount: number;
  netTotal: number;
  customerCount: number;
}

export interface CafeteriaOverviewGradeRow {
  gradeLevel: number;
  orderCount: number;
  netTotal: number;
  customerCount: number;
}

export interface CafeteriaOverview {
  preset: CafeteriaOverviewPreset;
  granularity: CafeteriaOverviewGranularity;
  range: CafeteriaOverviewDateSpan;
  previousRange: CafeteriaOverviewDateSpan;
  comparisonRange: CafeteriaOverviewDateSpan;
  totals: CafeteriaOverviewTotals;
  previousTotals: CafeteriaOverviewTotals;
  series: CafeteriaOverviewBucket[];
  previousSeries: CafeteriaOverviewBucket[];
  byHour: CafeteriaOverviewHour[];
  byWeekday: CafeteriaOverviewWeekday[];
  byCustomerType: CafeteriaOverviewCustomerTypeRow[];
  byGrade: CafeteriaOverviewGradeRow[];
  products: ProductSalesRow[];
  generatedAt: string;
}

export interface CafeteriaOverviewFilters {
  preset: CafeteriaOverviewPreset;
  from?: string;
  to?: string;
}

export const cafeteriaOverviewApi = {
  overview: (filters: CafeteriaOverviewFilters) => {
    const params = new URLSearchParams({ preset: filters.preset });
    if (filters.preset === 'custom') {
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
    }
    return fetchApi<CafeteriaOverview>(`${BASE}/overview?${params.toString()}`);
  },
};
